import React from 'react';
import { bool, func, object, shape, string } from 'prop-types';

import TabPanel, { Tab } from '../../components/TabPanel';
import ModifyRuleFieldset from './ModifyRuleFieldset';
import RuleTargetsFieldset from './RuleTargetsFieldset';
import RuleFaultLogicFieldset from './RuleFaultLogicFieldset';
import RuleFormulaFieldset from './RuleFormulaFieldset';
import RulePropertiesFieldset from './RulePropertiesFieldset';
import RuleScoreFieldset from './RuleScoreFieldset';
import RuleSensorsFieldset from './RuleSensorsFieldset';
import usePermissions from '../../hooks/usePermissions';

import defaultClasses from './RulesForm.module.css';

/**
 * Returns various states of the form based on inputs
 */
function useDerivedFormState({
    assignedRule,
    errors,
    isFormikSubmitting,
    rule,
    ruleTemplates,
    touched,
    values
}) {
    const isFullRulesAllowed = usePermissions('rulesFull');

    // Property Tab is in an error state if any of the validated fields it
    // contains has errors.
    const hasPropertyError =
        Boolean(touched.priority && errors.priority);

    // Form is disabled if there is a problem loading the rule
    const isAssignedRuleDisabled = assignedRule.loading || Boolean(assignedRule.error);
    const isRuleDisabled = rule.loading || Boolean(rule.error);
    const isDisabled = isAssignedRuleDisabled || isRuleDisabled;

    // Persisted non-custom K2A rules
    const isPersistedK2aRule =
        (Boolean(values.ruleTemplateId) || values.id) &&
        !values.custom;

    const isSubmitting = isFormikSubmitting ||
        assignedRule.saving ||
        isFormikSubmitting;

    // Form is readOnly if modifications are not allowed
    const isReadOnly = !isFullRulesAllowed ||
        isDisabled ||
        isPersistedK2aRule ||
        isFormikSubmitting;

    const isLoading = assignedRule.loading ||
        rule.loading ||
        ruleTemplates.loading;

    return {
        hasPropertyError,
        isDisabled,
        isLoading,
        isReadOnly,
        isSubmitting
    };
}

const RulesForm = ({
    assignedRule,
    companyInfo,
    errors,
    id,
    isFormikSubmitting,
    onBlur,
    onCancel,
    onChange,
    onFormikSubmit,
    onPatch,
    onReset,
    onSubmit,
    rule,
    ruleTemplates,
    selectedTab,
    setFieldValue,
    touched,
    values
}) => {
    const {
        hasPropertyError,
        isDisabled,
        isLoading,
        isReadOnly,
        isSubmitting
    } = useDerivedFormState({
        assignedRule,
        errors,
        touched,
        isFormikSubmitting,
        rule,
        ruleTemplates,
        values
    });

    const renderScoreTab = () => {
        if (process.env.ruleBuilderScoreEnabled) {
            return (
                <Tab
                    href="score"
                    title="Score"
                >
                    <RuleScoreFieldset
                        disabled={isReadOnly}
                        errors={errors}
                        onBlur={onBlur}
                        onChange={onChange}
                        onPatch={onPatch}
                        setFieldValue={setFieldValue}
                        touched={touched}
                        values={values}
                    />
                </Tab>
            );
        }
    };

    return (
        <form
            className={defaultClasses.root}
            id={id}
            onReset={onReset}
            onSubmit={onSubmit}
        >
            <aside className={defaultClasses.sidebar}>
                <ModifyRuleFieldset
                    disabled={isDisabled}
                    errors={errors}
                    isLoading={isLoading}
                    onBlur={onBlur}
                    onCancel={onCancel}
                    onChange={onChange}
                    onPatch={onPatch}
                    readOnly={isReadOnly}
                    ruleTemplates={ruleTemplates}
                    touched={touched}
                    values={values}
                    submitting={isSubmitting}
                />
                <RuleTargetsFieldset
                    disabled={isReadOnly}
                    companyId={companyInfo.companyId}
                    errors={errors}
                    onBlur={onBlur}
                    onChange={onChange}
                    onPatch={onPatch}
                    touched={touched}
                    values={values}
                />
            </aside>
            <TabPanel
                aria-label="Rule Builder Options"
                selectedTab={selectedTab}
            >
                <Tab
                    error={hasPropertyError}
                    href="properties"
                    title="Properties"
                >
                    <RulePropertiesFieldset
                        disabled={isReadOnly}
                        errors={errors}
                        onBlur={onBlur}
                        onChange={onChange}
                        onPatch={onPatch}
                        touched={touched}
                        values={values}
                    />
                </Tab>
                <Tab href="sensor filters" title="Sensor Filters">
                    <RuleSensorsFieldset
                        disabled={isDisabled}
                        onBlur={onBlur}
                        onPatch={onPatch}
                        readOnly={isReadOnly}
                        values={values}
                    />
                </Tab>
                <Tab
                    error={Boolean(errors.formulaCode)}
                    href="formula"
                    title="Formula"
                >
                    <RuleFormulaFieldset
                        disabled={isReadOnly}
                        isLoading={rule.loading}
                        errors={errors}
                        onPatch={onPatch}
                        touched={touched}
                        values={values}
                    />
                </Tab>
                { renderScoreTab() }
                <Tab
                    error={Boolean(errors.faultLogicCode)}
                    href="fault-logic"
                    title="Fault Logic"
                >
                    <RuleFaultLogicFieldset
                        disabled={isReadOnly}
                        errors={errors}
                        isLoading={rule.loading}
                        onPatch={onPatch}
                        touched={touched}
                        values={values}
                    />
                </Tab>
            </TabPanel>
        </form>
    );
};

RulesForm.defaultProps = {
    assignedRule: {
        saving: false
    },
    companyInfo: {},
    rule:        {
        error:   '',
        data:    {},
        loading: false
    },
    ruleTemplates: {
        loading: false
    }
};

RulesForm.propTypes = {
    assignedRule: shape({
        saving: bool
    }),
    companyInfo: shape({
        companyId: string
    }),
    id:             string,
    onFormikSubmit: func,
    onChange:       func,
    onPatch:        func,
    onSubmit:       func,
    rule:           shape({
        data:    object,
        error:   string,
        loading: bool
    }),
    ruleTemplates: shape({
        loading: bool
    }),
    selectedTab: string
};

export default RulesForm;
